import { useCallback, useEffect, useRef, useState } from 'react';
import { classifyMediaError, extractErrorMessage, MediaErrorType } from '../../../utils/classifyMediaError';
import {
  checkMediaPermissionState,
  PermissionCheckResult,
  watchPermissionState,
} from '../../../utils/checkMediaPermissionState';
import { captureError } from '../../../utils/sentry';

export type DeviceCheckStatus = 'idle' | 'checking' | 'done';

export interface DeviceCheckError {
  type: MediaErrorType | 'unknown';
  message: string;
}

export interface UseDeviceCheckReturn {
  status: DeviceCheckStatus;
  /** Set when the CAMERA specifically failed — drives the "Camera access blocked" row. */
  cameraError: DeviceCheckError | null;
  /** Set when the MICROPHONE specifically failed — drives the "Microphone access blocked" row. Independent of cameraError. */
  micError: DeviceCheckError | null;
  /** Live camera/mic permission state, or null when it's still loading OR when the Permissions API is unsupported — check permissionStateLoaded to tell those two apart. */
  permissionState: PermissionCheckResult | null;
  /** True once the permission check has resolved, whether or not the Permissions API turned out to be supported. Lets the caller distinguish "still loading" (permissionState null, not loaded) from "unsupported" (permissionState null, loaded) — both would otherwise look identical. */
  permissionStateLoaded: boolean;
  /**
   * Triggers the real getUserMedia() check. If permissions are already
   * granted, this doesn't prompt at all and is safe to call automatically
   * (as RoomNameScreen does). Otherwise it does trigger the real OS
   * permission dialog, so only call it from an explicit user gesture (e.g. a
   * button click) — never automatically — per the priming-pattern design.
   */
  requestAccess: () => Promise<void>;
}

function stopStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => track.stop());
}

function toError(err: unknown): { deviceError: DeviceCheckError; category: string; domExceptionName?: string } {
  const classified = classifyMediaError(err);
  return {
    deviceError: {
      type: classified?.errorType ?? 'unknown',
      message: extractErrorMessage(err),
    },
    category: classified?.category ?? 'camera_microphone_device_error',
    domExceptionName: classified?.domExceptionName,
  };
}

interface AttemptDiagnostics {
  /** Live Permissions API state read just before the request (null when unsupported). */
  stateBefore: PermissionCheckResult | null;
  /** How long the combined request took to reject — this is the call the user actually answers a dialog for. */
  combinedMs: number;
  /** How long the per-device probes took. */
  probeMs: number;
}

// A getUserMedia() rejection that arrives within this many ms almost certainly
// came back without any dialog being shown: the browser already had a stored
// "block", an OS-level or enterprise policy denied it, etc. A real person
// reading and answering a prompt takes longer. This is a heuristic, not a
// browser API — it exists so Sentry can separate "customer said no just now"
// from "browser was already set to no", which the DOMException alone can't.
const PROMPT_SHOWN_THRESHOLD_MS = 300;

function reportDeviceError(err: unknown, device: 'camera' | 'microphone', diagnostics: AttemptDiagnostics) {
  const { deviceError, category, domExceptionName } = toError(err);
  const { stateBefore, combinedMs, probeMs } = diagnostics;
  captureError(
    err,
    category,
    {
      device_check_error_type: deviceError.type,
      device_check_device: device,
      phase: 'preflight',
      permission_state_before: stateBefore?.[device] ?? 'unknown',
      prompt_shown: combinedMs >= PROMPT_SHOWN_THRESHOLD_MS ? 'likely' : 'unlikely',
      ...(domExceptionName ? { dom_exception_name: domExceptionName } : {}),
    },
    { combined_ms: Math.round(combinedMs), probe_ms: Math.round(probeMs) }
  );
}

/**
 * Pre-flight camera/mic permission check for the join screen. This is a
 * yes/no access check, not a preview — there's no way to hand a pre-acquired
 * stream to Zoom's own startVideo() (it does its own independent
 * getUserMedia() call and takes no parameters), so holding tracks open here
 * would only risk a "device in use" conflict with zero benefit. Every track
 * acquired for the check is stopped immediately once we know whether it
 * succeeded — nothing is exposed to the caller beyond pass/fail per device.
 *
 * Deliberately does not call getUserMedia() on its own — permissionState lets
 * the caller decide whether to skip a priming step (already granted), and
 * requestAccess() is only ever invoked in response to an explicit user
 * gesture, per the priming-pattern design (see the plan: immediate
 * auto-prompting caused users to mis-click "Block").
 *
 * Camera and microphone outcomes are reported fully independently: a single
 * combined getUserMedia() rejects if EITHER device fails, and the rejection
 * doesn't say which. On failure we probe the camera and mic separately, in
 * parallel, so a mic-only problem never reads as a camera problem and vice
 * versa.
 */
export default function useDeviceCheck(): UseDeviceCheckReturn {
  const [status, setStatus] = useState<DeviceCheckStatus>('idle');
  const [cameraError, setCameraError] = useState<DeviceCheckError | null>(null);
  const [micError, setMicError] = useState<DeviceCheckError | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionCheckResult | null>(null);
  const [permissionStateLoaded, setPermissionStateLoaded] = useState(false);
  const isMountedRef = useRef(true);
  // Mirror of permissionState for requestAccess(), which is memoised with no
  // deps and would otherwise only ever see the initial null.
  const permissionStateRef = useRef<PermissionCheckResult | null>(null);
  useEffect(() => {
    permissionStateRef.current = permissionState;
  }, [permissionState]);

  useEffect(() => {
    // permissionState stays null for this browser's entire session when the
    // Permissions API is entirely absent — matching checkMediaPermissionState()'s
    // contract elsewhere, so callers can keep treating "the whole API is
    // missing" and "a specific permission is merely 'unsupported'" as distinct.
    if (!navigator.permissions?.query) {
      // Deferred to a microtask rather than set synchronously: this mirrors
      // the real (async) branch below closely enough that "loaded" never
      // observably flips before the caller's very next tick, regardless of
      // which branch of this check applies.
      Promise.resolve().then(() => {
        if (isMountedRef.current) setPermissionStateLoaded(true);
      });
      return;
    }

    // watchPermissionState reports the initial value AND live updates from the
    // same query — this is what lets the caller (RoomNameScreen) notice the
    // user fixing a denied permission in their browser's site settings while
    // the page is still open, and recover automatically, instead of requiring
    // them to know to manually reload. permissionStateLoaded only flips once
    // BOTH have reported at least once.
    let cameraReported = false;
    let micReported = false;
    const markLoadedOnceBothReport = () => {
      if (cameraReported && micReported && isMountedRef.current) setPermissionStateLoaded(true);
    };

    const unsubscribeCamera = watchPermissionState('camera' as PermissionName, (camera) => {
      if (!isMountedRef.current) return;
      cameraReported = true;
      setPermissionState((prev) => ({ camera, microphone: prev?.microphone ?? 'unsupported' }));
      markLoadedOnceBothReport();
    });
    const unsubscribeMicrophone = watchPermissionState('microphone' as PermissionName, (microphone) => {
      if (!isMountedRef.current) return;
      micReported = true;
      setPermissionState((prev) => ({ camera: prev?.camera ?? 'unsupported', microphone }));
      markLoadedOnceBothReport();
    });

    return () => {
      unsubscribeCamera();
      unsubscribeMicrophone();
    };
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const requestAccess = useCallback(async () => {
    setStatus('checking');
    setCameraError(null);
    setMicError(null);

    const stateBefore = permissionStateRef.current;
    const startedAt = performance.now();

    // 1) Combined request — one prompt covers both devices in the common case.
    try {
      const combined = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // The check is pass/fail only — nothing needs the tracks once we know
      // they were granted, so stop them right away rather than holding the
      // camera/mic open until the user actually joins.
      stopStream(combined);
      if (isMountedRef.current) setStatus('done');
      return;
    } catch {
      if (!isMountedRef.current) return;
      // Fall through: the combined rejection doesn't say which device caused
      // it, so probe each independently, in parallel, below.
    }
    const combinedMs = performance.now() - startedAt;

    // 2) Probe camera and mic independently so each is attributed correctly.
    // Each call is deferred inside its own .then() so a *synchronous* throw
    // (e.g. navigator.mediaDevices being undefined) becomes a rejection of
    // just that probe instead of throwing while the array literal is still
    // being evaluated — which would otherwise skip the other probe entirely
    // and reject requestAccess() as a whole.
    const safeGetUserMedia = (constraints: MediaStreamConstraints) =>
      Promise.resolve().then(() => navigator.mediaDevices.getUserMedia(constraints));

    const probeStartedAt = performance.now();
    const [videoResult, audioResult] = await Promise.allSettled([
      safeGetUserMedia({ video: true }),
      safeGetUserMedia({ audio: true }),
    ]);
    const probeMs = performance.now() - probeStartedAt;
    const diagnostics: AttemptDiagnostics = { stateBefore, combinedMs, probeMs };

    if (videoResult.status === 'fulfilled') stopStream(videoResult.value);
    if (audioResult.status === 'fulfilled') stopStream(audioResult.value);

    if (!isMountedRef.current) return;

    let nextCameraError: DeviceCheckError | null = null;
    let nextMicError: DeviceCheckError | null = null;

    if (videoResult.status === 'rejected') {
      nextCameraError = toError(videoResult.reason).deviceError;
      reportDeviceError(videoResult.reason, 'camera', diagnostics);
    }

    if (audioResult.status === 'rejected') {
      nextMicError = toError(audioResult.reason).deviceError;
      reportDeviceError(audioResult.reason, 'microphone', diagnostics);
    }

    setCameraError(nextCameraError);
    setMicError(nextMicError);

    // An explicit "Block" updates navigator.permissions synchronously — no
    // reload needed to observe it. Re-querying right after a permission
    // failure lets the caller (RoomNameScreen) tell "now confirmed denied"
    // apart from "still prompt" (e.g. the dialog was dismissed/ignored rather
    // than explicitly blocked), which a reload alone can't fix either way but
    // which the UI treats differently.
    if (nextCameraError?.type === 'permissionDenied' || nextMicError?.type === 'permissionDenied') {
      const refreshedState = await checkMediaPermissionState();
      if (isMountedRef.current && refreshedState) setPermissionState(refreshedState);
    }

    // The await above opens an async gap since the isMountedRef check earlier
    // in this function — guard this setter too, like every other post-await
    // setter in this hook, so an unmount during the re-query can't still
    // trigger a state update.
    if (isMountedRef.current) setStatus('done');
  }, []);

  return { status, cameraError, micError, permissionState, permissionStateLoaded, requestAccess };
}
